import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { PageHeader, PillBadge, GhostButton } from "../components/Primitives";
import StickyFiltersBar, { useFullscreenRoute } from "../components/StickyFiltersBar";
import { useRememberWindowGeometry } from "../lib/popoutGeometry";
import { Clock, Phone, RefreshCw, BellOff, AlarmClockOff, Radio, ShieldOff, CheckCircle2, Loader2, Maximize2, Minimize2, UserPlus, MapPin, Download, ExternalLink, Check } from "lucide-react";
import { SortableHeader, useSortableRows } from "../components/SortableTable";
import { toast } from "sonner";
import { NewDispatchModal } from "../components/dispatch/NewDispatchModal";
import { AssignShiftModal } from "../components/tardies/AssignShiftModal";
import { KioskIdleOverlay } from "../components/KioskIdleOverlay";
import { fmtDate, fmtTime } from "../lib/tz";

const POLL_MS = 60000;
const EMPTY = { client_names: [], site_ids: [], guard_ids: [] };

// Two distinct tardy streams — kept apart because they represent different
// failure modes and route to different ops playbooks:
//   • shift: guard didn't clock in on time (chase-down phone call)
//   • status_check: guard missed a mid-shift welfare acknowledgement (welfare check)
const TABS = [
  { key: "shift",             label: "Shift Tardies",         Icon: Clock },
  { key: "status_check",      label: "Missed Status Checks",  Icon: ShieldOff },
  { key: "missed_tour_stops", label: "Missed Tour Stops",     Icon: MapPin },
  // iter244 — Live geofence breaches migrated in from /geofence so ops
  // can triage every "guard-off-post" signal from a single queue. The
  // /geofence page itself is now the historical view.
  { key: "geofence",          label: "Geofence Breaches",     Icon: MapPin },
];

export default function Tardies() {
  const location = useLocation();
  const isFs = useFullscreenRoute(location.pathname);
  useRememberWindowGeometry(isFs ? location.pathname : null);
  // Read `?tab=` + `?when=` from the URL so deep-links (e.g. the Overview
  // "Missed Tour Alerts (Today)" tile) land on the correct sub-tab and
  // pre-filtered date range.
  const initialTab = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const t = q.get("tab");
    return ["shift", "status_check", "missed_tour_stops", "geofence"].includes(t) ? t : "shift";
  }, [location.search]);
  const initialMtsRange = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const today = new Date().toISOString().slice(0, 10);
    if (q.get("when") === "today") return { start: today, end: today };
    const end = new Date();
    const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  }, [location.search]);
  const [tab, setTab] = useState(initialTab);
  const [rows, setRows] = useState([]);
  const [scRows, setScRows] = useState([]);  // status-check missed rows
  const [mtsRows, setMtsRows] = useState([]);  // missed-tour-stops (iter 208)
  const [gfRows, setGfRows] = useState([]);   // open geofence breaches (iter 244)
  const [mtsRange, setMtsRange] = useState(initialMtsRange);
  const [silenced, setSilenced] = useState(new Set());
  const [snoozed, setSnoozed] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const [filters, setFilters] = useState(EMPTY);
  const [dispatchFor, setDispatchFor] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  // iter244 — Track seen breach IDs across polls so we can pop a toast
  // for genuinely new open breaches (skips the initial load).
  const seenBreachIdsRef = useRef(null);

  const load = useCallback(async () => {
    const [tRes, sRes, zRes, scRes, gfRes] = await Promise.all([
      api.get("/tardies"),
      api.get("/tardies/silenced").catch(() => ({ data: [] })),
      api.get("/tardies/snoozed").catch(() => ({ data: [] })),
      api.get("/tardies/status-checks").catch(() => ({ data: [] })),
      api.get("/geofence/breaches", { params: { status: "open" } }).catch(() => ({ data: [] })),
    ]);
    setRows(tRes.data);
    setScRows(scRes.data || []);
    const nextBreaches = gfRes.data || [];
    setGfRows(nextBreaches);
    // iter244 — Fire a toast for every NEW open breach that wasn't in
    // the previous snapshot. The very first load just seeds the set so
    // dispatchers don't get spammed for already-existing breaches on page
    // load / hard refresh.
    if (seenBreachIdsRef.current === null) {
      seenBreachIdsRef.current = new Set(nextBreaches.map((b) => b.id));
    } else {
      for (const b of nextBreaches) {
        if (!seenBreachIdsRef.current.has(b.id)) {
          seenBreachIdsRef.current.add(b.id);
          toast.warning(
            `Geofence breach · ${b.guard_name || "Officer"} @ ${b.site_name || "site"}`,
            {
              description: `${b.max_distance_m ?? "?"}m from site (fence ${b.fence_m ?? "?"}m). Click to review.`,
              duration: 12000,
              action: {
                label: "View",
                onClick: () => setTab("geofence"),
              },
            },
          );
        }
      }
    }
    setSilenced(new Set((sRes.data || []).map((p) => `${p.shift_id}:${p.guard_id}`)));
    setSnoozed(new Map((zRes.data || []).map((p) => [`${p.shift_id}:${p.guard_id}`, p])));
    setLoading(false);
    setLastFetch(new Date());
  }, []);

  // Missed tour stops — fetch on tab focus + when range changes.
  const loadMts = useCallback(async () => {
    try {
      const params = {
        start: new Date(mtsRange.start + "T00:00:00Z").toISOString(),
        end: new Date(mtsRange.end + "T23:59:59Z").toISOString(),
      };
      const res = await api.get("/reports/missed-tour-stops", { params });
      setMtsRows(res.data?.rows || []);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load missed tour stops"));
    }
  }, [mtsRange]);
  useEffect(() => { if (tab === "missed_tour_stops") loadMts(); }, [tab, loadMts]);

  const silenceRow = useCallback(async (r) => {
    try {
      await api.post(`/tardies/${r.shift_id}/${r.guard_id}/silence`);
      setSilenced((prev) => new Set(prev).add(`${r.shift_id}:${r.guard_id}`));
      toast.success(`Silenced tardy alerts for ${r.guard_name}`);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to silence"));
    }
  }, []);

  const snoozeRow = useCallback(async (r) => {
    try {
      const res = await api.post(`/tardies/${r.shift_id}/${r.guard_id}/snooze`);
      const info = res.data || {};
      setSnoozed((prev) => {
        const n = new Map(prev);
        n.set(`${r.shift_id}:${r.guard_id}`, {
          shift_id: r.shift_id,
          guard_id: r.guard_id,
          next_alert_min: info.next_alert_min,
        });
        return n;
      });
      if (info.snoozed > 0) {
        toast.success(
          info.next_alert_min
            ? `Snoozed — next alert at ${info.next_alert_min} min late`
            : `Snoozed — no more alerts will fire`
        );
      } else {
        toast(`Nothing to snooze — next alert already beyond the 15-min window`);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Failed to snooze"));
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const acknowledgeBreach = useCallback(async (b) => {
    try {
      await api.post(`/geofence/breaches/${b.id}/acknowledge`);
      toast.success(`Acknowledged · ${b.guard_name}`);
      // Optimistically drop the row — the open-status filter will exclude
      // acknowledged/closed rows on the next poll regardless.
      setGfRows((prev) => prev.filter((r) => r.id !== b.id));
    } catch (e) {
      toast.error(errorMessage(e, "Failed to acknowledge"));
    }
  }, []);

  const toneFor = (m) => (m >= 60 ? "red" : m >= 30 ? "amber" : "default");

  const filtered = useMemo(() => {
    const { client_names, site_ids, guard_ids } = filters;
    if (!client_names.length && !site_ids.length && !guard_ids.length) return rows;
    const cset = new Set(client_names), sset = new Set(site_ids), gset = new Set(guard_ids);
    return rows.filter((r) =>
      (cset.size === 0 || cset.has(r.client_name)) &&
      (sset.size === 0 || sset.has(r.site_id)) &&
      (gset.size === 0 || gset.has(r.guard_id))
    );
  }, [rows, filters]);

  const sortAccessors = useMemo(() => ({
    guard: (r) => (r.guard_name || "").toLowerCase(),
    site: (r) => (r.site_name || "").toLowerCase(),
    client: (r) => (r.client_name || "").toLowerCase(),
    scheduled_start: (r) => r.shift_start || "",
    minutes_late: (r) => Number(r.minutes_late) || 0,
  }), []);
  const sortState = useSortableRows(filtered, sortAccessors, { defaultKey: "minutes_late", defaultDir: "desc", storageKey: "tardies" });

  const containerClass = isFs ? "fixed inset-0 z-50 bg-[#0A0A0A] overflow-auto" : "";

  return (
    <div className={containerClass} data-testid="tardies-page">
      <KioskIdleOverlay enabled={isFs} label="Tardies · Live" />
      {!isFs && (
        <PageHeader
          title="Tardies"
          subtitle="Live —  who haven't clocked in to a currently running shift. 15-minute grace; auto-refreshes every 60s."
          action={
            <div className="inline-flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1" data-testid="tardies-refresh-indicator">
                <RefreshCw className="size-3" /> {lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : "Loading…"}
              </div>
              <GhostButton data-testid="tardies-refresh-button" onClick={load}>Refresh</GhostButton>
              <GhostButton
                data-testid="tardies-fullscreen-toggle"
                onClick={() => window.open("/tardies/fullscreen", "_blank", "noopener")}
                title="Open in a new window"
              >
                <Maximize2 className="size-3" /> Fullscreen
              </GhostButton>
            </div>
          }
        />
      )}
      {isFs && (
        <div className="sticky top-0 z-30 flex items-center justify-end gap-2 px-4 py-2 border-b border-[#27272A] bg-[#0A0A0A]/95 backdrop-blur-md">
          <GhostButton data-testid="tardies-refresh-button" onClick={load}>
            <RefreshCw className="size-3" /> Refresh
          </GhostButton>          <GhostButton
            data-testid="tardies-exit-fullscreen"
            onClick={() => window.close()}
            title="Close this window"
          >
            <Minimize2 className="size-3" /> Close
          </GhostButton>
        </div>
      )}
      <div className={isFs ? "p-6 space-y-3" : "p-8 space-y-4"}>
        {/* Tab switcher — keeps shift-tardies and missed-status-checks
            visually distinct so ops don't blend the minutes. */}
        <div className="inline-flex border border-[#27272A] bg-[#0F0F0F]" data-testid="tardies-tabs">
          {TABS.map(({ key, label, Icon }) => {
            const active = tab === key;
            const count = key === "shift" ? rows.length
              : key === "status_check" ? scRows.length
              : key === "geofence" ? gfRows.length
              : mtsRows.length;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                data-testid={`tardies-tab-${key}`}
                className={`px-4 py-2 text-[11px] uppercase tracking-widest font-mono transition-colors border-r border-[#27272A] last:border-r-0 inline-flex items-center gap-2 ${
                  active ? "bg-[#FF3B30]/10 text-[#FF3B30]" : "text-zinc-400 hover:text-white"
                }`}
              >
                <Icon className="size-3.5" />
                <span>{label}</span>
                <span className={`ml-1 font-black ${active ? "text-[#FF3B30]" : "text-zinc-500"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {tab === "shift" && (
        <>
        <StickyFiltersBar
          testIdPrefix="tardies"
          value={filters}
          onChange={setFilters}
          visibleCount={filtered.length}
          totalCount={rows.length}
          collapsible
        />

        <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
          <table className="w-full text-sm" data-testid="tardies-table">
            <thead className="border-b border-[#27272A]">
              <tr>
                <SortableHeader k="guard" state={sortState}>Officer</SortableHeader>
                <SortableHeader k="site" state={sortState}>Site</SortableHeader>
                <SortableHeader k="client" state={sortState}>Client</SortableHeader>
                <SortableHeader k="scheduled_start" state={sortState}>Scheduled Start</SortableHeader>
                <SortableHeader k="minutes_late" state={sortState}>Minutes Late</SortableHeader>
                <th className="px-4 py-3 text-left"><div className="label-mute">Contact</div></th>
                <th className="px-4 py-3 text-right"><div className="label-mute">Actions</div></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {loading && <tr><td colSpan={7} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>}
              {!loading && sortState.sorted.length === 0 && (
                <tr><td colSpan={7} className="text-center py-16 text-zinc-500 scanlines">
                  {rows.length > 0 ? "No tardies match the filters." : "All clear — no tardies right now."}
                </td></tr>
              )}
              {sortState.sorted.map((r) => {
                const pairKey = `${r.shift_id}:${r.guard_id}`;
                const isOpen = !!r.is_open_shift || r.guard_id === "_open_";
                const isSilenced = silenced.has(pairKey);
                const snoozeRaw = snoozed.get(pairKey);
                // Snooze is only "active" while the guard hasn't yet reached the next alert threshold.
                // If they've passed it, the snooze window is effectively over — show the Snooze button
                // again so ops can snooze the NEXT threshold.
                const snoozeActive = snoozeRaw && (
                  snoozeRaw.next_alert_min == null
                    ? r.minutes_late <= (snoozeRaw.max_snoozed_threshold || 0)
                    : r.minutes_late < snoozeRaw.next_alert_min
                );
                return (
                <tr key={pairKey} className="hover:bg-[#1c1c1e]" data-testid={`tardy-${r.shift_id}-${r.guard_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-display font-bold uppercase tracking-tight flex items-center gap-2">
                      {r.guard_name}
                      {isOpen && (
                        <PillBadge tone="amber" data-testid={`tardy-open-badge-${r.shift_id}`}>OPEN · UNASSIGNED</PillBadge>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{r.badge_no || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{r.site_name}</td>
                  <td className="px-4 py-3 text-zinc-400">{r.client_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-zinc-400">
                    {fmtTime(r.shift_start, r.site_timezone, { hour: "2-digit", minute: "2-digit", ...(r.site_timezone ? { timeZoneName: "short" } : {}) })}
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{fmtDate(r.shift_start, r.site_timezone)}</div>
                  </td>
                  <td className="px-4 py-3" data-testid={`mins-late-${r.shift_id}-${r.guard_id}`}>
                    <PillBadge tone={toneFor(r.minutes_late)}>
                      <Clock className="size-3 mr-1" /> {r.minutes_late} min late
                    </PillBadge>
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-400">
                    {r.phone ? <a className="hover:text-[#0A84FF] inline-flex items-center gap-1" href={`tel:${r.phone}`}><Phone className="size-3" />{r.phone}</a> : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isOpen ? (
                      <div className="inline-flex items-center gap-2">
                        <GhostButton
                          data-testid={`tardy-assign-${r.shift_id}`}
                          onClick={() => setAssignFor(r)}
                          title="Assign a  to this open shift"
                          className="!border-[#FF3B30]/60 !text-[#FF3B30] hover:!bg-[#FF3B30]/10"
                        >
                          <UserPlus className="size-3" /> Assign
                        </GhostButton>
                        <GhostButton
                          data-testid={`tardy-dispatch-${r.shift_id}-${r.guard_id}`}
                          onClick={() => setDispatchFor(r)}
                          title={`Send a dispatch blast for ${r.site_name}`}
                        >
                          <Radio className="size-3" /> Dispatch
                        </GhostButton>
                        <GhostButton
                          data-testid={`tardy-silence-${r.shift_id}-${r.guard_id}`}
                          onClick={() => silenceRow(r)}
                          title="Stop tardy alerts for this open shift"
                        >
                          <BellOff className="size-3" /> Silence
                        </GhostButton>
                      </div>
                    ) : isSilenced ? (
                      <PillBadge tone="default" data-testid={`tardy-silenced-${r.shift_id}-${r.guard_id}`}>
                        <BellOff className="size-3 mr-1" /> Silenced
                      </PillBadge>
                    ) : (
                      <div className="inline-flex items-center gap-2">
                        {snoozeActive ? (
                          <PillBadge tone="amber" data-testid={`tardy-snoozed-${r.shift_id}-${r.guard_id}`}>
                            <AlarmClockOff className="size-3 mr-1" />
                            {snoozeRaw.next_alert_min ? `Snoozed → ${snoozeRaw.next_alert_min}m` : "Snoozed"}
                          </PillBadge>
                        ) : (
                          <GhostButton
                            data-testid={`tardy-snooze-${r.shift_id}-${r.guard_id}`}
                            onClick={() => snoozeRow(r)}
                            title="Skip tardy alert emails for 15 minutes"
                          >
                            <AlarmClockOff className="size-3" /> Snooze 15m
                          </GhostButton>
                        )}
                        <GhostButton
                          data-testid={`tardy-dispatch-${r.shift_id}-${r.guard_id}`}
                          onClick={() => setDispatchFor(r)}
                          title={`Send a dispatch blast to guards assigned to ${r.site_name}`}
                        >
                          <Radio className="size-3" /> Dispatch
                        </GhostButton>
                        <GhostButton
                          data-testid={`tardy-silence-${r.shift_id}-${r.guard_id}`}
                          onClick={() => silenceRow(r)}
                          title="Stop all further tardy notification emails for this shift"
                        >
                          <BellOff className="size-3" /> Silence
                        </GhostButton>
                      </div>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
        </>
        )}

        {tab === "status_check" && (
          <StatusCheckTardiesTable
            rows={scRows}
            loading={loading}
            onDispatch={setDispatchFor}
            onResolved={(promptId) => setScRows((prev) => prev.filter((x) => x.prompt_id !== promptId))}
          />
        )}

        {tab === "missed_tour_stops" && (
          <MissedTourStopsTable
            rows={mtsRows}
            range={mtsRange}
            onRangeChange={setMtsRange}
          />
        )}

        {tab === "geofence" && (
          <GeofenceBreachesTable
            rows={gfRows}
            loading={loading}
            onDispatch={setDispatchFor}
            onAcknowledge={acknowledgeBreach}
          />
        )}
      </div>

      {dispatchFor && (
        <NewDispatchModal
          onClose={() => setDispatchFor(null)}
          onSent={() => setDispatchFor(null)}
          restrictToSite={{ id: dispatchFor.site_id, name: dispatchFor.site_name }}
          prefill={{
            subject: dispatchFor._gf
              ? `Geofence breach · ${dispatchFor.guard_name} @ ${dispatchFor.site_name}`
              : `Tardy · ${dispatchFor.guard_name} @ ${dispatchFor.site_name}`,
            message: dispatchFor._gf
              ? `${dispatchFor.guard_name} left the ${dispatchFor.site_name} geofence — currently ${dispatchFor.max_distance_m}m from site (fence ${dispatchFor.fence_m}m). Please investigate and make contact.`
              : dispatchFor._sc
                ? `${dispatchFor.guard_name} missed a status check at ${dispatchFor.site_name} ${dispatchFor.minutes_late} min ago and hasn't responded. Can anyone available run a welfare check?`
                : `${dispatchFor.guard_name} is ${dispatchFor.minutes_late} min late for their shift at ${dispatchFor.site_name}. Can anyone available cover or make contact?`,
          }}
        />
      )}
      {assignFor && (
        <AssignShiftModal
          row={assignFor}
          onClose={() => setAssignFor(null)}
          onAssigned={() => {
            // Refresh the tardies list — the just-assigned row should
            // disappear (guard now on the shift) and any legacy dedupe
            // rows carry the `_open_` sentinel so they don't affect the
            // new assignment's escalation ladder.
            load();
          }}
        />
      )}
    </div>
  );
}


// ─── Missed Status-Check tardy table ─────────────────────────────
function StatusCheckTardiesTable({ rows, loading, onDispatch, onResolved }) {
  const [resolveFor, setResolveFor] = useState(null);
  const accessors = useMemo(() => ({
    guard:                (r) => (r.guard_name || "").toLowerCase(),
    site:                 (r) => (r.site_name || "").toLowerCase(),
    client:               (r) => (r.client_name || "").toLowerCase(),
    scheduled_at:         (r) => r.scheduled_at || "",
    minutes_since_missed: (r) => Number(r.minutes_since_missed) || 0,
  }), []);
  const sortState = useSortableRows(rows, accessors, {
    defaultKey: "minutes_since_missed", defaultDir: "asc", storageKey: "sc-tardies",
  });
  const toneFor = (m) => (m >= 30 ? "red" : m >= 10 ? "amber" : "default");

  return (
    <>
    <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
      <table className="w-full text-sm" data-testid="sc-tardies-table">
        <thead className="border-b border-[#27272A]">
          <tr>
            <SortableHeader k="guard"                state={sortState}>Officer</SortableHeader>
            <SortableHeader k="site"                 state={sortState}>Site</SortableHeader>
            <SortableHeader k="client"               state={sortState}>Client</SortableHeader>
            <SortableHeader k="scheduled_at"         state={sortState}>Scheduled Check</SortableHeader>
            <SortableHeader k="minutes_since_missed" state={sortState}>Missed Since</SortableHeader>
            <th className="px-4 py-3 text-left"><div className="label-mute">Contact</div></th>
            <th className="px-4 py-3 text-right"><div className="label-mute">Actions</div></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#27272A]">
          {loading && (
            <tr><td colSpan={7} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>
          )}
          {!loading && sortState.sorted.length === 0 && (
            <tr><td colSpan={7} className="text-center py-16 text-zinc-500 scanlines">
              No missed status checks in the last 24 h — nice.
            </td></tr>
          )}
          {sortState.sorted.map((r) => (
            <tr key={r.prompt_id} className="hover:bg-[#1c1c1e]" data-testid={`sc-tardy-${r.prompt_id}`}>
              <td className="px-4 py-3">
                <div className="font-display font-bold uppercase tracking-tight">{r.guard_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{r.badge_no || "—"}</div>
              </td>
              <td className="px-4 py-3 text-zinc-300">{r.site_name}</td>
              <td className="px-4 py-3 text-zinc-400">{r.client_name || "—"}</td>
              <td className="px-4 py-3 font-mono text-zinc-400">
                {r.scheduled_at ? fmtTime(r.scheduled_at, r.site_timezone, { hour: "2-digit", minute: "2-digit", ...(r.site_timezone ? { timeZoneName: "short" } : {}) }) : "—"}
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {r.scheduled_at ? fmtDate(r.scheduled_at, r.site_timezone) : ""}
                </div>
              </td>
              <td className="px-4 py-3" data-testid={`sc-mins-${r.prompt_id}`}>
                <PillBadge tone={toneFor(r.minutes_since_missed)}>
                  <ShieldOff className="size-3 mr-1" />
                  {r.minutes_since_missed} min missed
                </PillBadge>
              </td>
              <td className="px-4 py-3 font-mono text-zinc-400">
                {r.phone
                  ? <a className="hover:text-[#0A84FF] inline-flex items-center gap-1" href={`tel:${r.phone}`}>
                      <Phone className="size-3" />{r.phone}
                    </a>
                  : "—"}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex items-center gap-2">
                  <GhostButton
                    data-testid={`sc-resolve-${r.prompt_id}`}
                    onClick={() => setResolveFor(r)}
                    title="Mark this missed check as resolved ( was contacted / verified safe)"
                    className="!border-[#34C759]/50 !text-[#34C759]"
                  >
                    <CheckCircle2 className="size-3" /> Resolve
                  </GhostButton>
                  <GhostButton
                    data-testid={`sc-dispatch-${r.prompt_id}`}
                    onClick={() => onDispatch({
                      guard_name: r.guard_name,
                      site_name: r.site_name,
                      site_id: r.site_id,
                      minutes_late: r.minutes_since_missed,
                      _sc: true,
                    })}
                    title={`Send a welfare-check dispatch to guards at ${r.site_name}`}
                  >
                    <Radio className="size-3" /> Welfare Check
                  </GhostButton>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {resolveFor && (
      <ResolveScTardyModal
        row={resolveFor}
        onClose={() => setResolveFor(null)}
        onResolved={(pid) => { setResolveFor(null); onResolved?.(pid); }}
      />
    )}
    </>
  );
}


function ResolveScTardyModal({ row, onClose, onResolved }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/tardies/status-checks/${row.prompt_id}/resolve`, { note: note || null });
      toast.success(`Missed check resolved · ${row.guard_name}`);
      onResolved?.(row.prompt_id);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to resolve"));
    }
    setBusy(false);
  };
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="sc-resolve-modal"
    >
      <div
        className="w-full max-w-md border border-[#34C759]/40 bg-[#0F0F0F]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[#27272A] px-5 py-4 flex items-center gap-2">
          <CheckCircle2 className="size-5 text-[#34C759]" />
          <div>
            <div className="label-mute text-[#34C759]">Resolve Missed Check</div>
            <div className="font-display uppercase text-lg font-bold tracking-tight">
              {row.guard_name} · {row.site_name}
            </div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-zinc-300">
            Marks this missed check as resolved. The compliance metric still
            counts it as a miss (audit-safe), but the row drops out of the
            live tardy queue.
          </p>
          <div>
            <div className="label-mute mb-1">Resolution note (recommended)</div>
            <textarea
              data-testid="sc-resolve-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="How was contact made? What was the reason for the miss?"
              className="w-full bg-[#0A0A0A] border border-[#27272A] text-sm p-2 focus:border-[#34C759] outline-none"
              maxLength={500}
            />
          </div>
          <div className="flex justify-end gap-2">
            <GhostButton onClick={onClose} disabled={busy}>Cancel</GhostButton>
            <GhostButton
              onClick={submit}
              disabled={busy}
              data-testid="sc-resolve-submit"
              className="!border-[#34C759] !text-[#34C759]"
            >
              {busy ? <Loader2 className="size-3 animate-spin" /> : <CheckCircle2 className="size-3" />} Resolve
            </GhostButton>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Missed Tour Stops (iter 208) — historical view driven by the
// `/reports/missed-tour-stops` endpoint. Filters: date range + site
// via the existing StickyFiltersBar pattern (kept minimal here).
// ─────────────────────────────────────────────────────────────────
function MissedTourStopsTable({ rows, range, onRangeChange }) {
  const csvHref = useMemo(() => {
    const params = new URLSearchParams({
      start: new Date(range.start + "T00:00:00Z").toISOString(),
      end: new Date(range.end + "T23:59:59Z").toISOString(),
      format: "csv",
    });
    return `/api/reports/missed-tour-stops?${params.toString()}`;
  }, [range]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="label-mute mb-1">From</div>
          <input
            data-testid="mts-range-start"
            type="date"
            value={range.start}
            onChange={(e) => onRangeChange({ ...range, start: e.target.value })}
            className="bg-black/40 border border-[#27272A] px-3 py-2 text-sm"
          />
        </div>
        <div>
          <div className="label-mute mb-1">To</div>
          <input
            data-testid="mts-range-end"
            type="date"
            value={range.end}
            onChange={(e) => onRangeChange({ ...range, end: e.target.value })}
            className="bg-black/40 border border-[#27272A] px-3 py-2 text-sm"
          />
        </div>
        <div className="ml-auto">
          <a
            href={csvHref}
            data-testid="mts-export-csv"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#27272A] hover:border-[#FF3B30] text-xs uppercase tracking-widest text-zinc-300 hover:text-[#FF3B30] transition-colors"
          >
            <Download className="size-3.5" /> CSV
          </a>
        </div>
      </div>
      <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
        <table className="w-full text-sm" data-testid="mts-table">
          <thead className="border-b border-[#27272A]">
            <tr className="text-left text-zinc-400">
              <th className="px-4 py-3 font-normal">Window End</th>
              <th className="px-4 py-3 font-normal">Site</th>
              <th className="px-4 py-3 font-normal">Client</th>
              <th className="px-4 py-3 font-normal">Officer</th>
              <th className="px-4 py-3 font-normal">Badge</th>
              <th className="px-4 py-3 font-normal text-right">Scans</th>
              <th className="px-4 py-3 font-normal text-right">Required</th>
              <th className="px-4 py-3 font-normal">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#27272A]">
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500 text-xs font-mono">No missed tour stops in this period.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#1c1c1e]" data-testid={`mts-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400 whitespace-nowrap">
                  {r.window_end ? new Date(r.window_end).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 font-medium">{r.site_name || "—"}</td>
                <td className="px-4 py-3 text-zinc-400">{r.client_name || "—"}</td>
                <td className="px-4 py-3">{r.guard_name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{r.badge_no || "—"}</td>
                <td className="px-4 py-3 font-mono text-right">{r.scans_in_window}</td>
                <td className="px-4 py-3 font-mono text-right text-zinc-500">{r.min_required || "—"}</td>
                <td className="px-4 py-3">
                  <PillBadge tone={r.status === "missing" ? "red" : "amber"}>{r.status}</PillBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Active Geofence Breaches (iter 244) — live queue of open breaches
// that used to live at /geofence. Mirrors the inline actions from
// that page (Map · Dispatch · Ack) so ops can triage in-place.
// ─────────────────────────────────────────────────────────────────
const fmtBreachDuration = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return rs ? `${m}m ${rs}s` : `${m}m`;
};

function GeofenceBreachesTable({ rows, loading, onDispatch, onAcknowledge }) {
  const accessors = useMemo(() => ({
    guard:            (r) => (r.guard_name || "").toLowerCase(),
    site:             (r) => (r.site_name || "").toLowerCase(),
    client:           (r) => (r.client_name || "").toLowerCase(),
    started_at:       (r) => r.started_at || "",
    max_distance_m:   (r) => Number(r.max_distance_m) || 0,
  }), []);
  const sortState = useSortableRows(rows, accessors, {
    defaultKey: "started_at", defaultDir: "desc", storageKey: "tardies-geofence",
  });

  return (
    <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
      <table className="w-full text-sm" data-testid="tardies-geofence-table">
        <thead className="border-b border-[#27272A]">
          <tr>
            <SortableHeader k="guard"          state={sortState}>Officer</SortableHeader>
            <SortableHeader k="site"           state={sortState}>Site</SortableHeader>
            <SortableHeader k="client"         state={sortState}>Client</SortableHeader>
            <SortableHeader k="started_at"     state={sortState}>Started</SortableHeader>
            <th className="px-4 py-3 text-left"><div className="label-mute">Duration</div></th>
            <SortableHeader k="max_distance_m" state={sortState}>Max distance</SortableHeader>
            <th className="px-4 py-3 text-right"><div className="label-mute">Actions</div></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#27272A]">
          {loading && (
            <tr><td colSpan={7} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>
          )}
          {!loading && sortState.sorted.length === 0 && (
            <tr><td colSpan={7} className="text-center py-16 text-zinc-500 scanlines">
              All clear — no officers currently outside their geofence.
            </td></tr>
          )}
          {sortState.sorted.map((b) => {
            const mapLink = `https://www.google.com/maps/search/?api=1&query=${b.last_lat ?? b.started_lat},${b.last_lng ?? b.started_lng}`;
            return (
              <tr key={b.id} className="hover:bg-[#1c1c1e]" data-testid={`tardies-geofence-row-${b.id}`}>
                <td className="px-4 py-3">
                  <div className="font-display font-bold uppercase tracking-tight">{b.guard_name || "—"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{b.badge_no || "—"}</div>
                </td>
                <td className="px-4 py-3 text-zinc-300">{b.site_name || "—"}</td>
                <td className="px-4 py-3 text-zinc-400">{b.client_name || "—"}</td>
                <td className="px-4 py-3 font-mono text-zinc-400">
                  {b.started_at ? new Date(b.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {b.started_at ? fmtDate(b.started_at) : ""}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-zinc-300">{fmtBreachDuration(b.duration_s)}</td>
                <td className="px-4 py-3">
                  <PillBadge tone={b.max_distance_m > 500 ? "red" : "amber"}>
                    <MapPin className="size-3 mr-1" /> {b.max_distance_m}m
                  </PillBadge>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">fence {b.fence_m}m</div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <a
                      href={mapLink}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 border border-[#27272A] text-[10px] uppercase tracking-widest text-zinc-300 hover:text-white hover:border-[#0A84FF]"
                      data-testid={`tardies-geofence-map-${b.id}`}
                    >
                      <ExternalLink className="size-3" /> Map
                    </a>
                    <GhostButton
                      data-testid={`tardies-geofence-dispatch-${b.id}`}
                      onClick={() => onDispatch({
                        guard_name: b.guard_name,
                        site_id: b.site_id,
                        site_name: b.site_name,
                        max_distance_m: b.max_distance_m,
                        fence_m: b.fence_m,
                        _gf: true,
                      })}
                      title={`Send a dispatch blast to guards assigned to ${b.site_name}`}
                    >
                      <Radio className="size-3" /> Dispatch
                    </GhostButton>
                    {!b.acknowledged_at && (
                      <GhostButton
                        data-testid={`tardies-geofence-ack-${b.id}`}
                        onClick={() => onAcknowledge(b)}
                        title="Mark as reviewed"
                      >
                        <Check className="size-3" /> Ack
                      </GhostButton>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
